import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { unProtectedOpenRoutes } from 'components/Routes/routePermissions';
import * as storage from '../services/authSessionStorage';
import * as PiNxtMock from '../../mocks/auth/pinxt/pinxt.service';
import { useAppConfig } from '../../hooks/useAppConfig';
import { Maybe } from '../../types/types.generated';
import {
  getLegacyToken,
  getIdTokenObj,
  isValidToken,
  hasTokenExpired,
} from '../utils/authSessionUtils';
import { useAuthService } from './useAuthService';
import QuantumService from '../services/quantumService';
import { IAnalyticLoginFailedEvent } from '../types/analytics';
import { IPiNxtAPIResponse } from '../../types/pinxt';
import { parsePiNxtPostEssoQuery } from '../utils/authUserUtils';
import { IPiAuthParams } from '../../types/authUser';

export interface ITokenState {
  token: Maybe<string>;
  tokenExp: Maybe<string>;
  refreshToken: Maybe<string>;
  idToken: Maybe<string>;
}

/**
 * Manages the users Auth Access token with PiNxt.
 * Don't use directly from within page components, instead use useAuthSession hook
 */
export const useAccessToken = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tokenState, setTokenState] = useState<ITokenState>({
    token: storage.getAccessToken(),
    refreshToken: storage.getRefreshToken(),
    idToken: storage.getIdToken(),
    tokenExp: storage.getAccessTokenExp(),
  });

  const {
    isMock,
    auth: { loginUrl, piEsso, targetBg },
    api: { auth: authHost },
  } = useAppConfig();
  const location = useLocation();

  const { token, tokenExp, refreshToken, idToken } = tokenState;

  const legacyToken = useMemo(() => getLegacyToken(idToken), [idToken]);
  const idTokenObj = useMemo(() => getIdTokenObj(idToken), [idToken]);

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);

  const { fetchTokensOnRefresh, fetchTokens, fetchAuthCode, fetchLogout } =
    useAuthService();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Sets new auth session
   * @param accessTkn set the user's authenticated JWT token
   * @param refreshTkn set the user's JWT token to be used onpage refresh
   * @param idTkn set the user's (unencrypted) JWT token used for SOME api requests still
   */
  const setSession = (accessTkn: string, refreshTkn: string, idTkn: string) => {
    // Save state to local storage
    storage.setAccessToken(accessTkn);
    storage.setRefreshToken(refreshTkn);
    storage.setIdToken(idTkn);

    // update context state
    setTokenState({
      token: storage.getAccessToken(),
      tokenExp: storage.getAccessTokenExp(),
      refreshToken: storage.getRefreshToken(),
      idToken: storage.getIdToken(),
    });

    setIsAuthenticated(true);
    setTokenRefreshing(false);
  };

  /**
   * Clear the entire auth session from local storage and reset state
   */
  const clearSession = useCallback(() => {
    storage.clearSessionStorageAndAuthTokens(isMock);

    setIsAuthenticated(false);
    setIsAuthenticating(false);
    setTokenRefreshing(false);

    // clear cache from Query data
    queryClient.removeQueries();
  }, [isMock, queryClient]);

  /**
   * Refreshes the current auth session by requesting new Auth Tokens
   */
  const refreshSession = useCallback(async (): Promise<void> => {
    setTokenRefreshing(true);

    // If no tokens, reset and void auth session.
    if (!refreshToken) {
      console.error('Unable to refresh tokens, no refresh token');
      clearSession();
      window.location.href = `${loginUrl}&login-error=true`;
      return;
    }

    try {
      const response = await fetchTokensOnRefresh(refreshToken);

      if (response.resultCode !== '200') {
        console.error('Unable to refresh auth tokens, logging out.');
        QuantumService.refreshAuth({
          eventInfo: {
            appErrorCode: response.resultCode,
          },
        });
        clearSession();
        window.location.href = `${loginUrl}&login-error=true`;
        return;
      }

      setSession(
        response.access_token,
        response.refresh_token!,
        response.id_token!
      );

      QuantumService.refreshAuth(
        {
          eventInfo: {},
        },
        response.id_token!,
        idTokenObj
      );
    } catch {
      console.error('Unable to refresh auth tokens, logging out.');
      QuantumService.refreshAuth({
        eventInfo: {
          appErrorCode: 'unknown', // TODO: Add FE code on Verbatim Integration
        },
      });
      clearSession();
      window.location.href = `${loginUrl}&login-error=true`;
    }
  }, [clearSession, fetchTokensOnRefresh, idTokenObj, loginUrl, refreshToken]);

  /**
   * Starts a auth session with a PiNXt supplied Auth Token
   * @param authCode PiNXt supplied Auth Token
   */
  const authenticateWithCode = useCallback(
    async (authCode: string): Promise<void> => {
      setIsAuthenticated(false);
      setIsAuthenticating(true);
      setIsAuthError(false);

      try {
        const res = await fetchTokens(authCode);

        if (!res || res.resultCode !== '200') {
          return await Promise.reject(new Error('fetchTokens'));
        }

        setSession(res.access_token, res.refresh_token!, res.id_token!);
        setIsAuthenticating(false);

        return await Promise.resolve();
      } catch (error) {
        setIsAuthenticating(false);
        setIsAuthError(true);
        return Promise.reject(error);
      }
    },
    [fetchTokens]
  );

  /**
   * Starts a auth session by kicking off the PiNxt authentication handshake.
   * @param username User's username
   * @param password User's password
   */
  const authenticateViaCreds = useCallback(
    async (username: string, password: string): Promise<void> => {
      setIsAuthenticated(false);
      setIsAuthenticating(true);
      setIsAuthError(false);

      QuantumService.loginStart({
        eventInfo: { loginType: 'manualAuth', username },
      });

      try {
        const res = await fetchAuthCode(username, password);

        if (!res || res.resultCode !== '200') {
          await Promise.reject(new Error('fetchAuthCode'));
        }

        return await authenticateWithCode(res.authCode);
      } catch (error: unknown) {
        setIsAuthenticating(false);
        setIsAuthError(true);

        const piNxtResError = error as IPiNxtAPIResponse;
        const quantumLoginFailed: IAnalyticLoginFailedEvent = {
          eventInfo: {
            loginType: 'manualAuth',
            appErrorCode: piNxtResError.resultCode,
            appErrorMessage: piNxtResError.resultMessage,
          },
          userInfo: null,
        };
        QuantumService.loginStop(quantumLoginFailed);

        return Promise.reject(error);
      }
    },
    [authenticateWithCode, fetchAuthCode]
  );

  /**
   * Process a ESSO redirect from PiNxt
   */
  const processEssoRedirect = useCallback(
    async (piNxtParams: IPiAuthParams) => {
      const { code: piNxtTransferCode } = piNxtParams;

      // The analyticsContext will handle the trackLoginStop when user info is loaded
      QuantumService.loginStart({
        eventInfo: { loginType: 'ssoAuth', username: '' },
      });

      try {
        await authenticateWithCode(piNxtTransferCode!);
        setIsLoading(false);
        // navigate through to the home page
        navigate('/');
      } catch {
        QuantumService.loginStop({
          eventInfo: {
            loginType: 'ssoAuth',
            appErrorCode: 'unknown',
            appErrorMessage: 'unknown',
          },
          userInfo: null,
        });
        clearSession();
        window.location.href = `${loginUrl}&login-error=true`;
      }
    },
    [authenticateWithCode, clearSession, loginUrl, navigate]
  );

  /** Starts an auth session by using Charter's ESSO Flow  */
  const authenticateViaEsso = useCallback(() => {
    const { clientId, redirectUri } = piEsso;
    const qsParams = [
      `client_id=${clientId}`,
      `target_bg=${targetBg}`,
      `redirect_uri=${redirectUri}`,
      `state=state`,
    ];
    return window.location.assign(`${authHost}/login?${qsParams.join('&')}`);
  }, [authHost, piEsso, targetBg]);

  /**
   * Sign the user out and clear auth session
   */
  const signOut = useCallback(async () => {
    try {
      await fetchLogout(token!);
    } catch (err) {
      console.error(err);
    } finally {
      QuantumService.logout();
      clearSession();
      // navigate to logout page
      window.location.href = '/logout';
    }
  }, [clearSession, fetchLogout, loginUrl, token]);

  /**
   * Check to see if the Access Token is expired, if so try to refresh it.
   */
  const refreshTokenIfExpired = useCallback(() => {
    if (token && hasTokenExpired(tokenExp!) && !tokenRefreshing) {
      return refreshSession();
    }

    return Promise.resolve();
  }, [refreshSession, token, tokenExp, tokenRefreshing]);

  /**
   * Run once on Component Load
   */
  useEffect(() => {
    // check for a internal redirect PiNxt code
    const piNxtParams = parsePiNxtPostEssoQuery();

    // Handle ESSO PINxt post redirect.
    if (piNxtParams.code) {
      processEssoRedirect(piNxtParams).then(
        () => {
          // do nothing
        },
        () => {
          // do nothing
        }
      );
      return;
    }

    /**
     * Since OpsCenter requires Auth to access, for mock inject the info in
     * if it doesn't exist
     */
    if (isMock && !token) {
      const authTokens = PiNxtMock.getTokens();
      setSession(
        authTokens.access_token,
        authTokens.refresh_token!,
        authTokens.id_token!
      );
      window.location.href = '/'; // refresh page
      return;
    }

    if (token) {
      // Check validity of token
      if (!isValidToken(token, isMock)) {
        clearSession();
        if (unProtectedOpenRoutes.includes(location.pathname)) {
          setIsLoading(false);
        } else {
          window.location.href = loginUrl;
          return;
        }
      }

      // Next check to be sure the token hasn't expired
      refreshTokenIfExpired().then(
        () => {
          // Token is valid, auth session ready to go
          setIsAuthenticated(true);
          setIsLoading(false);
        },
        () => {
          // do nothing
        }
      );
      return;
    }

    // No token, user isn't authenticated, redirect to login
    if (unProtectedOpenRoutes.includes(location.pathname)) {
      setIsLoading(false);
    } else {
      authenticateViaEsso();
    }

    // Disable lint check as I want this to run once on component load
  }, []);

  return {
    isLoading,
    accessToken: token,
    accessTokenExp: tokenExp,
    refreshToken,
    idToken,
    idTokenObj,
    legacyToken,

    isAuthenticating,
    isAuthenticated,
    isAuthError,

    clearAuthSession: clearSession,
    signOut,
    authenticateWithCode,
    authenticateViaCreds,
    authenticateViaEsso,
    refreshTokenIfExpired,
  };
};
